import React, { useEffect, useState, useCallback, useRef } from "react";
import { Link } from "react-router-dom";
import { Bell } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api.js";

/**
 * Bell with unread badge. Polls every 25s while mounted.
 * Triggers browser Notification + toast for any newly-arrived items
 * (vs the previously-known unread count).
 */
export default function NotificationBell() {
  const [count, setCount] = useState(0);
  const lastCount = useRef(null);
  const lastSeenId = useRef(null);

  const refresh = useCallback(async () => {
    try {
      const r = await api.get("/notifications/unread-count");
      const newCount = r.data.count;
      if (lastCount.current !== null && newCount > lastCount.current) {
        // fetch latest unread to push surface
        const list = await api.get("/notifications", { params: { limit: 5 } });
        const fresh = list.data.find((n) => !n.read && n.id !== lastSeenId.current);
        if (fresh) {
          lastSeenId.current = fresh.id;
          toast(fresh.title, { description: fresh.body });
          if (typeof Notification !== "undefined" && Notification.permission === "granted") {
            try { new Notification(fresh.title, { body: fresh.body }); } catch (e) {}
          }
        }
      }
      setCount(newCount);
      lastCount.current = newCount;
    } catch (e) {}
  }, []);

  useEffect(() => {
    refresh();
    const id = setInterval(refresh, 25000);
    // request permission once
    if (typeof Notification !== "undefined" && Notification.permission === "default") {
      try { Notification.requestPermission(); } catch (e) {}
    }
    return () => clearInterval(id);
  }, [refresh]);

  return (
    <Link
      to="/notifications"
      data-testid="notification-bell"
      className="relative w-9 h-9 rounded-full bg-[#142534] border border-[#1a2f40] flex items-center justify-center"
      aria-label="Notifications"
    >
      <Bell size={16} className="text-slate-200" />
      {count > 0 && (
        <span
          data-testid="notification-badge"
          className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-[#EF4444] text-white text-[10px] font-bold flex items-center justify-center"
        >
          {count > 9 ? "9+" : count}
        </span>
      )}
    </Link>
  );
}
